import { Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { medicalsApi } from "../../../../../../../api/medicalsApi";
import { useExecutor } from "../../../../../../../hooks/useExecutor";
import useNotifications from "../../../../../../../hooks/useNotifications";
import { useUnsavedWhileEditing } from "../../../../../../../hooks/useUnsavedWhileEditing";
import { CopilotManager } from "../../../../../../../managers/CopilotManager";
import {
  TCopilot,
  TCopilotBloodPressure,
  TCopilotNoteVital,
  TCopilotNoteVitals,
} from "../../../../../../../types/copilot";
import {
  useOrganization,
  useOrganizationReference,
} from "../../../../../../facility/hooks/useOrganization";
import { useCopilotClinicalContext } from "../../useCopilotClinicalContext";
import CopilotSectionAcceptButton from "./CopilotSectionAcceptButton";
import CopilotSectionEditedBadge from "./CopilotSectionEditedBadge";
import {
  getVitalsPopulateNotification,
  hasIncompleteBloodPressure,
  processCopilotVitals,
} from "../utils/processCopilotVitals";
import { withSectionHumanEditedExtension } from "../utils/copilotExtensions";

interface EditableVitalsProps {
  vitals: TCopilotNoteVitals;
  readOnly?: boolean;
}

function normalizeBP(bp: any): TCopilotBloodPressure {
  if (!bp)
    return {
      systolic: { value: "", unit: "mmHg" },
      diastolic: { value: "", unit: "mmHg" },
    };
  const systolic =
    typeof bp.systolic === "object" && bp.systolic !== null
      ? bp.systolic
      : { value: bp.systolic ?? "", unit: "mmHg" };
  const diastolic =
    typeof bp.diastolic === "object" && bp.diastolic !== null
      ? bp.diastolic
      : { value: bp.diastolic ?? "", unit: "mmHg" };
  return { systolic, diastolic };
}

function getVitalMeasurementDisplay(
  _key?: string,
  data?: TCopilotNoteVital | TCopilotBloodPressure | null,
) {
  if (!_key) return "Unknown measurement";
  if (!data) return `${_key}:`;

  let key = _key;
  switch (key) {
    case "bloodPressure":
      key = `Blood Pressure`;
      break;
    case "height":
      key = `Height`;
      break;
    case "weight":
      key = `Weight`;
      break;
    case "bloodGlucose":
      key = `Blood Glucose`;
      break;
    case "pulse":
      key = `Pulse`;
      break;
    case "respiratoryRate":
      key = `Respiratory Rate`;
      break;
    case "oxygenSaturation":
      key = `Oxygen Saturation`;
      break;
    case "temperature":
      key = `Temperature`;
      break;
    case "pain":
      key = `Pain`;
      break;
    case "waist":
      key = `Waist`;
      break;
    default:
      return "Unknown measurement";
  }

  if (_key === "bloodPressure") {
    const bp = normalizeBP(data);
    return bp?.systolic?.value && bp?.diastolic?.value
      ? `${key}: ${bp.systolic.value}/${bp.diastolic.value} ${bp.systolic.unit || bp.diastolic.unit}`
      : `${key}:`;
  }

  if (_key === "pain") {
    const pain = data as TCopilotNoteVital;
    if (!pain?.value) return `${key}:`;
    const durationText = pain?.unit ? `, Duration: ${pain.unit}` : "";
    return `${key}: Level: ${pain.value}/10${durationText}`;
  }

  data = data as TCopilotNoteVital;

  // Only show unit if there's actually a value
  if (!data?.value || data.value === "") {
    return `${key}:`;
  }

  const valueText = data.unit ? `${data.value} ${data.unit}` : data.value;
  return `${key}: ${valueText}`;
}

const UNIT_OPTIONS: Record<string, string[]> = {
  temperature: ["°F", "°C"],
  height: ["cm", "in"],
  weight: ["lbs", "kg"],
  bloodGlucose: ["mg/dL", "mmol/L"],
  waist: ["cm", "in"],
};

function getDefaultUnit(vitalKey: string): string {
  return UNIT_OPTIONS[vitalKey]?.[0] || "";
}

function formatVitalInputValue(value: unknown): string {
  if (value == null || value === "") return "";
  return String(value);
}

function buildEditedVitals(
  vitals?: TCopilotNoteVitals | null,
): Pick<TCopilotNoteVitals, keyof TCopilotNoteVitals> {
  return {
    bloodPressure: normalizeBP(vitals?.bloodPressure),
    temperature: vitals?.temperature ?? { value: "", unit: "°F" },
    pulse: vitals?.pulse ?? { value: "", unit: "bpm" },
    respiratoryRate: vitals?.respiratoryRate ?? {
      value: "",
      unit: "breaths/min",
    },
    oxygenSaturation: vitals?.oxygenSaturation ?? { value: "", unit: "%" },
    height: vitals?.height ?? { value: "", unit: "in" },
    weight: vitals?.weight ?? { value: "", unit: "lbs" },
    bloodGlucose: vitals?.bloodGlucose ?? { value: "", unit: "mg/dL" },
    waist: vitals?.waist ?? { value: "", unit: "cm" },
    pain: vitals?.pain ?? { value: "", unit: "" },
  };
}

const EditableVitals = ({ vitals, readOnly = false }: EditableVitalsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  useUnsavedWhileEditing("copilot-note-vitals", isEditing);
  const organizationReference = useOrganizationReference();
  const { countryCode } = useOrganization();
  const { executeCallback } = useExecutor();

  const [editedVitals, setEditedVitals] = useState(() =>
    buildEditedVitals(vitals),
  );
  const [isSaving, setIsSaving] = useState(false);
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { setNotification } = useNotifications();

  useEffect(() => {
    if (!isEditing) {
      setEditedVitals(buildEditedVitals(vitals));
    }
  }, [vitals, isEditing]);

  const handleEditClick = () => {
    if (!copilotData) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!copilotData?.id) return;

    if (hasIncompleteBloodPressure(editedVitals)) {
      setNotification({
        message: "Enter both systolic and diastolic blood pressure values.",
        variant: "error",
      });
      return;
    }

    try {
      setIsSaving(true);

      const mergedVitals = {
        ...(copilotData.note?.vitals || {}),
        ...processCopilotVitals(editedVitals, copilotData.note?.vitals),
      };

      const updatedNote: TCopilot = withSectionHumanEditedExtension(
        {
          ...copilotData,
          note: {
            ...copilotData.note,
            vitals: mergedVitals,
          },
        },
        "vitals",
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      const savedCopilot = response.data;
      setCopilotData(savedCopilot);
      setEditedVitals(buildEditedVitals(savedCopilot?.note?.vitals));
      setNotification({
        message: "Vitals updated successfully",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Failed to update vitals",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleVitalChange = (key: string, value: any) => {
    setEditedVitals((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Vitals</Typography>
          <CopilotSectionEditedBadge section="vitals" />
          {!isEditing && !readOnly && (
            <IconButton size="small" onClick={handleEditClick}>
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {!isEditing && !readOnly && hasLinkedEncounter && (
          <CopilotSectionAcceptButton
            sections={["vitals"]}
            populationStatusField="vitals"
            onAccept={async () => {
              if (
                !encounterReference?.identifier ||
                !organizationReference?.identifier
              ) {
                setNotification({
                  message:
                    "Link this note to a medical encounter to apply content to the chart.",
                  variant: "error",
                });
                throw new Error("Encounter not linked");
              }

              const result = await CopilotManager.populateVitals(
                encounterReference.identifier,
                organizationReference.identifier,
                countryCode,
              );
              if (
                patientReference?.identifier &&
                encounterReference?.identifier
              ) {
                await executeCallback(
                  "copilot_refresh_vitals",
                  null,
                  patientReference.identifier,
                  encounterReference.identifier,
                );
              }
              const notification = getVitalsPopulateNotification(result);
              setNotification(notification);
              if (notification.variant !== "success") {
                throw new Error(notification.message);
              }
            }}
          />
        )}
      </Box>

      <Grid container spacing={1} sx={{ mt: 1 }}>
        {Object.entries(editedVitals || {}).map(([k, v]) => {
          const bpNormalized = k === "bloodPressure" ? normalizeBP(v) : null;
          const hasValue =
            k === "bloodPressure"
              ? bpNormalized?.systolic?.value || bpNormalized?.diastolic?.value
              : (v as TCopilotNoteVital)?.value;

          return (
            <Grid item xs={12} key={k}>
              {!isEditing ? (
                <Typography variant="body2">
                  {getVitalMeasurementDisplay(k, v)}
                </Typography>
              ) : k === "bloodPressure" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Blood Pressure:
                  </Typography>
                  <TextField
                    size="small"
                    label="Systolic"
                    value={formatVitalInputValue(bpNormalized?.systolic?.value)}
                    onChange={(e) =>
                      handleVitalChange(k, {
                        systolic: { value: e.target.value, unit: "mmHg" },
                        diastolic: bpNormalized?.diastolic || {
                          value: "",
                          unit: "mmHg",
                        },
                      })
                    }
                    sx={{ width: 100 }}
                  />
                  <TextField
                    size="small"
                    label="Diastolic"
                    value={formatVitalInputValue(
                      bpNormalized?.diastolic?.value,
                    )}
                    onChange={(e) =>
                      handleVitalChange(k, {
                        systolic: bpNormalized?.systolic || {
                          value: "",
                          unit: "mmHg",
                        },
                        diastolic: { value: e.target.value, unit: "mmHg" },
                      })
                    }
                    sx={{ width: 100 }}
                  />
                </Box>
              ) : k === "pain" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Pain Level:
                  </Typography>
                  <TextField
                    size="small"
                    label="Level (0-10)"
                    value={(v as TCopilotNoteVital)?.value || ""}
                    onChange={(e) =>
                      handleVitalChange(k, {
                        value: e.target.value,
                      })
                    }
                    sx={{ width: 100 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {startCase(k)}:
                  </Typography>
                  <TextField
                    size="small"
                    label="Value"
                    value={(v as TCopilotNoteVital)?.value || ""}
                    onChange={(e) =>
                      handleVitalChange(k, {
                        ...(v as TCopilotNoteVital),
                        value: e.target.value,
                      })
                    }
                    sx={{ width: 100 }}
                  />
                  {UNIT_OPTIONS[k] ? (
                    <Select
                      size="small"
                      value={
                        (v as TCopilotNoteVital)?.unit || getDefaultUnit(k)
                      }
                      onChange={(e) =>
                        handleVitalChange(k, {
                          ...(v as TCopilotNoteVital),
                          unit: e.target.value,
                        })
                      }
                      sx={{ width: 120 }}
                    >
                      {UNIT_OPTIONS[k].map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography variant="body2" sx={{ alignSelf: "center" }}>
                      {(v as TCopilotNoteVital)?.unit || getDefaultUnit(k)}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          );
        })}
      </Grid>

      {isEditing && (
        <Box
          sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          <Button
            size="small"
            onClick={() => {
              setEditedVitals(buildEditedVitals(vitals));
              setIsEditing(false);
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            size="small"
            variant="contained"
            onClick={handleSave}
            loading={isSaving}
          >
            Save Vitals
          </LoadingButton>
        </Box>
      )}
    </Box>
  );
};

export default EditableVitals;
